import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of bubbles, default 12 */
  count?: number;
  /** Bubble colour (rgba), default light blue */
  color?: string;
  /** Minimum bubble diameter px, default 5 */
  minSize?: number;
  /** Maximum bubble diameter px, default 14 */
  maxSize?: number;
  /** Base rise duration seconds, default 5 */
  dur?: number;
}

/**
 * Bubbles — rising circular bubbles with a gentle side-to-side wobble.
 * Good for plumbing, cleaning, or aquatic service banners.
 */
const Bubbles: React.FC<Props> = ({
  count = 12,
  color = "rgba(200,235,255,0.5)",
  minSize = 5,
  maxSize = 14,
  dur = 5,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      size: minSize + r(1) * (maxSize - minSize),
      d: dur * (0.8 + r(2) * 0.7),
      delay: r(3) * dur,
      wobble: 8 + r(4) * 10,
    }),
    [count, minSize, maxSize, dur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-bubble-rise {
          0%   { transform: translateY(10px); opacity: 0; }
          12%  { opacity: 0.9; }
          90%  { opacity: 0.7; }
          100% { transform: translateY(-105vh); opacity: 0; }
        }
        @keyframes oot-bubble-wobble {
          0%, 100% { margin-left: calc(var(--w) * -1); }
          50%       { margin-left: var(--w); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            bottom: 0,
            animation: `oot-bubble-rise ${p.d}s ease-in ${p.delay}s infinite`,
          }}
        >
          <div
            style={{
              animation: `oot-bubble-wobble ${p.d / 3}s ease-in-out infinite`,
              "--w": `${p.wobble}px`,
            } as React.CSSProperties}
          >
            <div
              style={{
                width: p.size,
                height: p.size,
                borderRadius: "50%",
                background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.9), ${color})`,
                border: `1px solid ${color}`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default Bubbles;
